import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, Card, Field, Input } from '@wundo/ui';
import { ApiError } from '@wundo/client/api.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import { suggestOrganisation, type OrganisationSuggestion } from '@/lib/organisations.ts';

export interface OrganisationLookupProps {
  onFound: (suggestion: OrganisationSuggestion) => void;
  onManual: () => void;
}

/* A refused schema names the field but carries no sentence fit to show. */
function lookupErrorMessage(caught: unknown): string {
  if (caught instanceof ApiError && caught.issues.length > 0) {
    return 'Enter a domain such as acme.be, an email address or a website.';
  }
  return apiErrorMessage(
    caught,
    'The lookup failed. Try again, or fill in the form manually.',
  );
}

/**
 * The first step of creating an organisation: one question, the customer's
 * domain. What comes back fills in the form, so nobody has to know what a
 * Teams tenant is or where to find it.
 */
export function OrganisationLookup({ onFound, onManual }: OrganisationLookupProps) {
  const [domain, setDomain] = useState('');
  const lookup = useMutation({
    mutationFn: () => suggestOrganisation(domain),
    onSuccess: onFound,
  });

  return (
    <form
      className='flex flex-col gap-5'
      onSubmit={(event) => {
        event.preventDefault();
        lookup.mutate();
      }}
    >
      <Card className='flex flex-col gap-5'>
        <Field
          label="Customer's domain"
          description='The part after the @ in their work email, such as acme.be. An email address or their website works too. We look up the rest and fill in the form for you to check.'
          error={lookup.isError ? lookupErrorMessage(lookup.error) : null}
        >
          {(control) => (
            <Input
              {...control}
              value={domain}
              required
              placeholder='acme.be'
              onChange={(event) => setDomain(event.target.value)}
            />
          )}
        </Field>

        <div className='flex items-center justify-between gap-3'>
          <Button variant='link' onClick={onManual}>
            Fill in manually
          </Button>
          <Button type='submit' loading={lookup.isPending}>
            {lookup.isPending ? 'Looking up…' : 'Look up'}
          </Button>
        </div>
      </Card>
    </form>
  );
}
